import { FC } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { Button, Card, Text, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { FieldList } from 'components/Admin/AdfoxInfo/AdfoxInfoResult/FieldList/FieldList';
import {
    buildAdfoxInfo,
    formatAdfoxInfoForCabinet,
    formatAdfoxInfoPretty,
} from 'components/Admin/AdfoxInfo/buildAdfoxInfo';

import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Admin/AdfoxInfo/AdfoxInfoResult/AdfoxInfoResult.module.less';

const TrlKeys = {
    title: 'admin.adfoxInfo.result.title',
    hint: 'admin.adfoxInfo.result.hint',
    empty: 'admin.adfoxInfo.result.empty',
    invalid: 'admin.adfoxInfo.result.invalid',
    unclosedQuote: 'admin.adfoxInfo.result.unclosedQuote',
    backslash: 'admin.adfoxInfo.result.backslash',
    pretty: 'admin.adfoxInfo.result.pretty',
    copy: 'admin.adfoxInfo.result.copy',
};

export const AdfoxInfoResult: FC = () => {
    const result = buildAdfoxInfo(useSelector((state) => state.adfoxInfo));

    const trls = useTrls();
    const dispatch = useDispatch();

    const cabinetValue = result.kind === 'json' ? formatAdfoxInfoForCabinet(result.value) : null;

    const copyToClipboard = async () => {
        if (cabinetValue === null) {
            return;
        }
        await navigator.clipboard.writeText(cabinetValue);
        dispatch(addMagritteNotification(MagritteNotificationName.AdfoxInfoCopyToClipboardSuccess));
    };

    return (
        <>
            <Text style="secondary" typography="title-5-semibold">
                {trls(TrlKeys.title)}
            </Text>
            <VSpacing default={16} />
            {result.kind === 'empty' && <Text data-qa="adfox-info-result-empty">{trls(TrlKeys.empty)}</Text>}
            {result.kind === 'invalid' && (
                <div data-qa="adfox-info-result-invalid">
                    <VSpacingContainer default={12}>
                        {result.missingFields.length > 0 && (
                            <FieldList
                                title={trls(TrlKeys.invalid)}
                                fields={result.missingFields}
                                dataQa="adfox-info-result-missing"
                            />
                        )}
                        {result.unclosedQuoteFields.length > 0 && (
                            <FieldList
                                title={trls(TrlKeys.unclosedQuote)}
                                fields={result.unclosedQuoteFields}
                                dataQa="adfox-info-result-unclosed-quote"
                            />
                        )}
                        {result.backslashFields.length > 0 && (
                            <FieldList
                                title={trls(TrlKeys.backslash)}
                                fields={result.backslashFields}
                                dataQa="adfox-info-result-backslash"
                            />
                        )}
                    </VSpacingContainer>
                </div>
            )}
            {result.kind === 'json' && cabinetValue !== null && (
                <VSpacingContainer default={16}>
                    <Text style="secondary" typography="paragraph-3-regular">
                        {trls(TrlKeys.hint)}
                    </Text>
                    <Card padding={16} borderRadius={16} style="secondary" stretched>
                        <pre className={styles.result} data-qa="adfox-info-result">
                            {cabinetValue}
                        </pre>
                    </Card>
                    <div>
                        <Button mode="primary" style="accent" onClick={copyToClipboard} data-qa="adfox-info-copy">
                            {trls(TrlKeys.copy)}
                        </Button>
                    </div>
                    <Text style="secondary" typography="paragraph-3-regular">
                        {trls(TrlKeys.pretty)}
                    </Text>
                    <Card padding={16} borderRadius={16} style="secondary" stretched>
                        <pre className={styles.result} data-qa="adfox-info-result-pretty">
                            {formatAdfoxInfoPretty(result.value)}
                        </pre>
                    </Card>
                </VSpacingContainer>
            )}
        </>
    );
};
